import { useEffect, useMemo } from "react";

import { useTranslation } from "react-i18next";
import { useLocation } from "react-router";

import useFAST from "../contexts/FASTContext";

import type { HeaderLayout } from "../contexts/FASTContext";
import type { Breadcrumbs } from "../types";

function useFASTLayout({ title, button, breadcrumbs }: HeaderLayout): void {
  const { state, dispatch } = useFAST();
  const { i18n, t } = useTranslation("editor-fast-channel");

  const path = useLocation().pathname;
  const isSamePage = state.layout?.header?.path === path;
  const channelData = state?.channelData;

  const memoBreadcrumbs = useMemo((): Breadcrumbs | undefined => {
    if (!breadcrumbs) {
      return undefined;
    }

    const breadcrumbsBase = [
      {
        name: t("fastChannels"),
        url: "/fast-channels",
      },
      {
        name: channelData?.name ?? "...",
        url: `/fast-channels/${channelData?.uuid}/distribute`,
      },
    ];

    return [...breadcrumbsBase, ...breadcrumbs];
  }, [channelData, t, breadcrumbs]);

  const areBreadcrumbsIdentical = useMemo(
    () =>
      memoBreadcrumbs
        ? memoBreadcrumbs.every(
            (breadcrumb, index) =>
              breadcrumb.name ===
              state.layout?.header?.breadcrumbs?.[index]?.name,
          )
        : true,
    [memoBreadcrumbs, state.layout?.header?.breadcrumbs],
  );

  const isSameLanguage = state.layout?.header?.lang === i18n.language;

  const blockDispatch = areBreadcrumbsIdentical && isSamePage && isSameLanguage;

  useEffect(() => {
    if (blockDispatch) return;

    dispatch({
      type: "SET_FAST_HEADER_LAYOUT",
      layout: {
        lang: i18n.language,
        path,
        title,
        breadcrumbs: memoBreadcrumbs,
        button,
      },
    });
  }, [
    dispatch,
    button,
    title,
    i18n.language,
    path,
    isSamePage,
    blockDispatch,
    memoBreadcrumbs,
  ]);
}

export default useFASTLayout;
